import { RRule } from "rrule";
import DOMPurify from "dompurify";
import ReactHtmlParser from "react-html-parser";
import { Box } from "@mui/material";
import dayjs from "dayjs";

// Add a hook to ensure security for target=_blank links
DOMPurify.addHook("afterSanitizeAttributes", function (node) {
  if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

export function formatDateString(dateString) {
  return dayjs(dateString).toDate().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function sanitizeHtml(html) {
  return (
    <Box
      sx={(theme) => ({
        "& a": {
          color: theme.palette.link.main,
          fontWeight: "bold",
          textDecoration: "none",
          "&:hover": {
            color: theme.palette.link.hovered,
          },
        },
        "& ul": {
          listStyleType: "square",
        },
      })}
    >
      {ReactHtmlParser(
        DOMPurify.sanitize(html, {
          USE_PROFILES: { html: true },
          ADD_ATTR: ["target"],
        })
      )}
    </Box>
  );
}

export function formatRecurrenceRule(recurrenceRule) {
  if (!recurrenceRule) {
    return;
  }
  try {
    const lines = recurrenceRule.split("\n");
    const ruleLine = lines.find((l) => l.startsWith("RRULE:"));
    if (ruleLine) {
      const ruleString = ruleLine.replace(/^RRULE:/, "");
      const rule = RRule.fromString(ruleString);
      return capitalize(rule.toText());
    }
  } catch (e) {
    console.error("Failed to parse recurrence:", e);
  }
  return "Repeats";
}
export function fullName(userBasedObj) {
  if (!userBasedObj) {
    return "";
  }
  if (!(userBasedObj?.first_name && userBasedObj?.last_name)) {
    return userBasedObj?.first_name || userBasedObj?.last_name || "";
  }
  return `${userBasedObj.first_name} ${userBasedObj.last_name}`;
}
export function camelToUnderscore(str) {
  return str.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase();
}

export function formatConstantOptions(constantsObj) {
  return Object.values(constantsObj).map((option) => {
    const label = option.split("_").map(capitalize).join(" ");
    return {
      label: label,
      value: option,
    };
  });
}

export function capitalize(word) {
  if (typeof word != "string") {
    return word;
  }
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

export function format6CharCode(code) {
  if (code?.length != 6) {
    throw new Error(`Cannot format code: ${code}`);
  }
  const firstPart = code.slice(0, 3);
  const secondPart = code.slice(3, 6);
  return `${firstPart}-${secondPart}`;
}

export function formatTitle(title) {
  if (!title) {
    return "";
  }
  return title.replace("_", " ").split(" ").map(capitalize).join(" ");
}

export function extractDigits(word) {
  if (!word) {
    return "";
  }

  const matches = word.toString().match(/(\d+)/);
  return matches ? matches[0] : "";
}

export function formatPhoneNumber(phoneNumber) {
  let digits = extractDigits(phoneNumber);
  if (digits.length > 10) {
    digits = digits.slice(digits.length - 10, digits.length);
  }
  if (![7, 10].includes(digits.length)) {
    return phoneNumber;
  }

  let areaCode = "";
  let mainPhoneNumberIdx = 0;
  if (digits.length == 10) {
    areaCode = "(" + digits.slice(0, 3) + ") ";
    mainPhoneNumberIdx = 3;
  }
  const mainPhoneNumber = `${digits.slice(
    mainPhoneNumberIdx,
    mainPhoneNumberIdx + 3
  )}-${digits.slice(mainPhoneNumberIdx + 3, mainPhoneNumberIdx + 7)}`;

  return areaCode + mainPhoneNumber;
}
export function stripParenthesizedWords(word) {
  let res = "";
  let inParen = false;
  for (let c of word) {
    if (c == "(") {
      inParen = true;
      continue;
    }
    if (c == ")") {
      inParen = false;
      continue;
    }
    if (!inParen) {
      res += c;
    }
  }

  return res.trim();
}

function extractTimeDetails(seconds) {
  const hours = parseInt(seconds / 3600);
  const minutes = parseInt((seconds % 3600) / 60);
  const newSeconds = parseInt(seconds % 60);
  return {
    hours,
    minutes,
    seconds: newSeconds,
  };
}

export function formatTime(seconds, short = true) {
  if (seconds <= 0) {
    return `0${short ? "s" : " seconds"}`;
  }

  const { hours, minutes, seconds: newSeconds } = extractTimeDetails(seconds);

  const timeElements = [];
  if (hours) {
    timeElements.push(
      `${hours}${short ? "h" : ` hour${hours > 1 ? "s" : ""}`}`
    );
  }
  if (minutes) {
    timeElements.push(
      `${minutes}${short ? "m" : ` minute${minutes > 1 ? "s" : ""}`}`
    );
  }
  if (newSeconds) {
    timeElements.push(
      `${newSeconds}${short ? "s" : ` second${seconds > 1 ? "s" : ""}`}`
    );
  }

  return timeElements.join(", ");
}

export function formatTimeForTimer(seconds) {
  const { hours, minutes, seconds: newSeconds } = extractTimeDetails(seconds);
  const elements = [
    hours.toString().padStart(2, "0"),
    minutes.toString().padStart(2, "0"),
    newSeconds.toString().padStart(2, "0"),
  ];
  return elements.join(":");
}

export function formatRecentTime(date) {
  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (isToday) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } else {
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  }
}
